import React, { ChangeEvent, ReactElement, useEffect } from 'react';
import { Typography, DialogContent, Grid2, Divider, TextField } from '@mui/material';
import { useAppDispatch, useAppSelector } from '../../../hooks/hooks';
import { importAllGoods } from '../../../redux/actions/goodsActions';
import { RootState } from '../../../redux/reducers';
import {
    clearGoodsForPurchaseOrder,
    clearPurchaseOrder,
    createNewEmptyPurchaseOrder,
    importPurchaseOrder,
    savePurchaseOrder,
    setPurchaseOrderDestinationSite,
    setPurchaseOrderSupplier,
    updatePurchaseOrderBatchArrivalTime,
    updatePurchaseOrderDocumentCode,
} from '../../../redux/actions/purchaseOrderActions';
import { getSuppliers } from '../../../redux/selectors/clientsSelector';
import { importSites } from '../../../redux/actions/siteActions';
import { importClients } from '../../../redux/actions/clientActions';
import { importArticles } from '../../../redux/actions/articleActions';
import { SaveEntityStatus } from '../../../redux/reducers/common';
import PurchaseOrderFormGoods from './PurchaseOrderFormGoods';
import dayjs, { Dayjs } from 'dayjs';
import { LocalizationProvider, DateTimePicker, renderTimeViewClock } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import ClientAutocomplete from '../../../common/components/autocomplete/ClientAutocomplete';
import SiteAutocomplete from '../../../common/components/autocomplete/SiteAutocomplete';

import { FormActions, FormDialogTitle } from '../../../common/components/form/FormPrimitives';

import { ResponsiveDialog } from '../../../common/components/mobile/MobilePrimitives';

type PurchaseOrderFormProps = {
    open: boolean;
    closeForm(): void;
    purchaseOrderId?: number;
};

export default function PurchaseOrderForm(props: PurchaseOrderFormProps): ReactElement {
    const { closeForm, open, purchaseOrderId } = props;

    const purchaseOrder = useAppSelector((state: RootState) => state.purchaseOrderForm.purchaseOrder);
    const uniqueBatchId = useAppSelector((state: RootState) => state.purchaseOrderForm.uniqueBatchId);
    const batchArrivalTime = useAppSelector((state: RootState) => state.purchaseOrderForm.batchArrivalTime);
    const saveStatus = useAppSelector((state: RootState) => state.purchaseOrderForm.saveStatus);
    const goodsForPurchaseOrder = useAppSelector(
        (state: RootState) => state.goodsToPurchaseOrderForm.goodBatchEntities,
    );

    const suppliers = useAppSelector(getSuppliers);
    const sites = useAppSelector((state: RootState) => state.sites.sites);
    const goods = useAppSelector((state: RootState) => state.goods.goods);
    const articles = useAppSelector((state: RootState) => state.articles.articles);

    const dispatch = useAppDispatch();

    useEffect(() => {
        if (open) {
            if (!!purchaseOrderId && purchaseOrderId > 0) {
                dispatch(importPurchaseOrder(purchaseOrderId));
            } else {
                if (!purchaseOrder) {
                    dispatch(createNewEmptyPurchaseOrder());
                }
            }
        }
    }, [dispatch, purchaseOrderId, open]);

    useEffect(() => {
        if (open) {
            if (!sites) {
                dispatch(importSites());
            }
        }
    }, [dispatch, open]);

    useEffect(() => {
        if (open) {
            if (!suppliers) {
                dispatch(importClients());
            }
        }
    }, [dispatch, open]);

    useEffect(() => {
        if (open) {
            if (!goods) {
                dispatch(importAllGoods());
            }
        }
    }, [dispatch, open]);

    useEffect(() => {
        if (open) {
            if (!articles) {
                dispatch(importArticles());
            }
        }
    }, [dispatch, open]);

    useEffect(() => {
        if (saveStatus === SaveEntityStatus.Saved) {
            dispatch(importAllGoods());
            handleClose();
        }
    }, [dispatch, saveStatus]);

    const handleSelectSupplier = (supplierId?: number) => {
        const supplier = suppliers?.find((s) => s.id === supplierId);

        dispatch(setPurchaseOrderSupplier(supplier));
    };

    const handleSelectDestinationSite = (siteId?: number) => {
        const site = sites?.find((s) => s.id === siteId);

        dispatch(setPurchaseOrderDestinationSite(site));
    };

    const handleBatchArrivalTimeChange = (date: Dayjs | null) => {
        dispatch(updatePurchaseOrderBatchArrivalTime(date ? date.toDate() : undefined));
    };

    const handleDocumentCodeChange = (event: ChangeEvent<HTMLInputElement>) => {
        const target = event.target;
        const value = target.value;

        dispatch(updatePurchaseOrderDocumentCode(value));
    };

    const handleSave = () => {
        dispatch(savePurchaseOrder());
    };

    const handleClose = () => {
        dispatch(clearPurchaseOrder());
        dispatch(clearGoodsForPurchaseOrder());
        closeForm();
    };

    const isOrderDataValid =
        !!purchaseOrder &&
        !!uniqueBatchId &&
        !(!purchaseOrder.supplierId || purchaseOrder.supplierId <= 0) &&
        !!purchaseOrder.destinationSiteId &&
        purchaseOrder.destinationSiteId > 0 &&
        !!purchaseOrder.documentCode &&
        purchaseOrder.documentCode.trim() !== '' &&
        !!goodsForPurchaseOrder &&
        goodsForPurchaseOrder.filter((gfpo) => !!gfpo.goods.article && !!gfpo.goods.quantity && gfpo.goods.quantity > 0)
            .length > 0 &&
        goodsForPurchaseOrder.filter(
            (gfpo) => !!gfpo.goods.article && (!gfpo.goods.quantity || gfpo.goods.quantity <= 0),
        ).length === 0;

    return (
        <ResponsiveDialog fullScreen scroll="paper" open={open} onClose={handleClose}>
            <FormDialogTitle onClose={handleClose}>
                Nalog za nabavku - {!purchaseOrderId ? 'kreiranje' : 'izmena'}
            </FormDialogTitle>
            <DialogContent dividers sx={{ px: { xs: 2, sm: 5 }, pt: { xs: 3, sm: 5 } }}>
                <Grid2 container spacing={2} style={{ margin: 'auto' }}>
                    <Grid2 size={{ xs: 12 }}>
                        <Typography variant="h6">Opšte informacije</Typography>
                    </Grid2>
                    <Grid2 size={{ xs: 12 }} sx={{ mb: 2 }}>
                        <Divider />
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                        <div>
                            <ClientAutocomplete
                                isRequired
                                clients={suppliers ?? []}
                                selectClient={handleSelectSupplier}
                                selectedClientId={purchaseOrder?.supplierId}
                            />
                        </div>
                    </Grid2>

                    <Grid2 size={{ xs: 12, sm: 6 }}>
                        <SiteAutocomplete
                            isRequired
                            selectSite={handleSelectDestinationSite}
                            sites={sites ?? []}
                            selectedSiteId={purchaseOrder?.destinationSiteId}
                            title="Odredište - skladište"
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                        <TextField
                            error={!purchaseOrder?.documentCode || purchaseOrder?.documentCode === ''}
                            required
                            fullWidth
                            name="title"
                            label="Br. predračuna/fakture/računa"
                            value={purchaseOrder?.documentCode ?? ''}
                            variant="outlined"
                            onChange={handleDocumentCodeChange}
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="sr">
                            <DateTimePicker
                                sx={{ width: '100%' }}
                                label="Ulazno vreme"
                                viewRenderers={{
                                    hours: renderTimeViewClock,
                                    minutes: renderTimeViewClock,
                                    seconds: renderTimeViewClock,
                                }}
                                value={batchArrivalTime ? dayjs(batchArrivalTime) : null}
                                onChange={(value) => handleBatchArrivalTimeChange(value)}
                            />
                        </LocalizationProvider>
                    </Grid2>
                    <Grid2 size={{ xs: 12 }} sx={{ mt: 2 }}>
                        <Typography variant="h6">Roba</Typography>
                    </Grid2>
                    <Grid2 size={{ xs: 12 }}>
                        <Divider />
                    </Grid2>
                    <Grid2 size={{ xs: 12 }}>
                        <PurchaseOrderFormGoods canChangeValuableInfo />
                    </Grid2>
                    <Grid2 size={{ xs: 0, sm: 12 }} style={{ height: 15, margin: 0, padding: 0 }} />
                    {!isOrderDataValid && (
                        <Grid2 size={{ xs: 12 }}>
                            <Typography color="error">*unesite sva obavezna polja</Typography>
                        </Grid2>
                    )}
                    <FormActions
                        onCancel={handleClose}
                        onSubmit={handleSave}
                        submitLabel="Sačuvaj"
                        disabled={!isOrderDataValid || saveStatus === SaveEntityStatus.Pending}
                        pending={saveStatus === SaveEntityStatus.Pending}
                    />
                </Grid2>
            </DialogContent>
        </ResponsiveDialog>
    );
}
